import { getCookie } from '../../helpers/cookieMetods';
import type { EntityId } from '../types/common';
import type {
  GalleryCatalogData,
  GalleryCatalogMutationResult,
  GalleryUploadResult,
} from '../types/gallery';
import { domain, parseJsonOrThrow } from './http';

export type UploadFileInGalleryOptions = {
  onUploadProgress?: (percent: number) => void;
};

/** Defensive parse for PHP list endpoints (may return non-array or sparse rows). */
export function normalizeGalleryCatalogList(
  raw: unknown
): GalleryCatalogData[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is GalleryCatalogData =>
      item != null && typeof item === 'object' && !Array.isArray(item)
  );
}

/**
 * `catfilesout.php` may return one catalog object or `[catalog]` (legacy array wrapper).
 */
export function normalizeGalleryCatalogDetail(
  raw: unknown
): GalleryCatalogData {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as GalleryCatalogData;
  }
  if (!Array.isArray(raw)) return {};
  const first = raw[0];
  if (first != null && typeof first === 'object' && !Array.isArray(first)) {
    return first as GalleryCatalogData;
  }
  return {};
}

export const createNewCatalogGallery = ({
  title,
  imgFile,
  date,
  autor_id,
}: {
  title: string;
  imgFile: (File | Blob | string)[];
  date: string;
  autor_id: EntityId;
}): Promise<GalleryCatalogMutationResult> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('title', title);
  data.append('create_date', date);
  data.append('autor_id', String(autor_id));
  for (let i = 0; i < imgFile.length; i++) {
    const file = imgFile[i];
    if (file != null)
      data.append('img[]', typeof file === 'string' ? file : file);
  }
  return fetch(`${domain}/php/createcat.php`, {
    method: 'POST',
    body: data,
  }).then((response) =>
    parseJsonOrThrow<GalleryCatalogMutationResult>(response)
  );
};

export const getGalleryCatalogs = (): Promise<GalleryCatalogData[]> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('out', 'yes');
  return fetch(`${domain}/php/createcat.php`, {
    method: 'POST',
    body: data,
  })
    .then((response) => parseJsonOrThrow<unknown>(response))
    .then(normalizeGalleryCatalogList);
};

export const getGalleryCatalogsByID = (
  catalog_id: string | number,
  file_type: string | null = null
): Promise<GalleryCatalogData> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('catalog_id', `${catalog_id}`);
  if (file_type) data.append('file_type', file_type);
  return fetch(`${domain}/php/catfilesout.php`, {
    method: 'POST',
    body: data,
  })
    .then((response) => parseJsonOrThrow<unknown>(response))
    .then(normalizeGalleryCatalogDetail);
};

export const updateCatalogGallery = ({
  title,
  id,
  imgFile,
  date,
}: {
  title: string;
  id: EntityId;
  imgFile: (File | Blob | string)[] | null | undefined;
  date: string;
}): Promise<Response> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('id', String(id));
  data.append('title', title);
  data.append('create_date', date);
  if (imgFile) {
    for (let i = 0; i < imgFile.length; i++) {
      const file = imgFile[i];
      if (file != null)
        data.append('img[]', typeof file === 'string' ? file : file);
    }
  }
  return fetch(`${domain}/php/editcat.php`, { method: 'POST', body: data });
};

export const deleteGalleryCatalogsFiles = (
  id: EntityId,
  target: string
): Promise<Response> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('id', String(id));
  data.append('target', target);
  return fetch(`${domain}/php/delcatorfile.php`, {
    method: 'POST',
    body: data,
  });
};

/**
 * Multipart upload to `upload.php`. Uses XHR (not fetch) to get
 * `xhr.upload.onprogress` events — the only standard API for upload progress.
 */
export const uploadFileInGallery = (
  id: EntityId,
  imgFile: File[] | Blob[] | null | undefined,
  options?: UploadFileInGalleryOptions
): Promise<GalleryUploadResult> => {
  const token = getCookie('_form_token');
  const data = new FormData();
  data.append('token', `${token}`);
  data.append('catalog_id', String(id));
  if (imgFile) {
    for (let i = 0; i < imgFile.length; i++) {
      data.append('img[]', imgFile[i]!);
    }
  }
  const onProgress = options?.onUploadProgress;
  return new Promise<GalleryUploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${domain}/php/upload.php`);
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve((JSON.parse(xhr.responseText) as GalleryUploadResult) ?? {});
        } catch {
          reject(
            new Error(`Upload response parse error (status ${xhr.status})`)
          );
        }
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload network error'));
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      };
    }
    xhr.send(data);
  });
};
